import * as THREE from 'three';
import {
  BOT_TIERS,
  Button,
  INPUT_BUFFER_SIZE,
  MAP_ROTATION,
  MAX_SHOT_DISTANCE,
  PLAYER_MAX_HEALTH,
  TICK_DT,
  WEAPONS,
  hasButton,
  isBotId,
  padGroups,
  vec3,
  weaponFromIndex,
  weaponIndex,
  type InputCommand,
  type MatchPhase,
  type PlayerId,
  type PlayerState,
  type SimEvents,
  type TeamId,
  type WeaponId,
} from '@game/shared';
import { GameSim, eyePosition } from '@game/sim';
import {
  ServerMessage,
  SnapshotFlag,
  type AnyServerMessage,
  type HubStateMessage,
  type MatchStateMessage,
  type SnapshotMessage,
  type SnapshotPlayer,
} from '@game/protocol';
import { GameConnection, type ConnectionStatus } from '../net/connection.js';
import { LocalAvatar } from '../render/avatar.js';
import { EffectsRenderer } from '../render/effects.js';
import { PlayerRenderer, type SpectatePose } from '../render/players.js';
import { SceneRenderer } from '../render/scene.js';
import { ViewModel } from '../render/viewmodel.js';
import { AudioEngine } from './audio.js';
import { DiagOverlay } from './diag.js';
import { InputManager } from './input.js';
import {
  SENSITIVITY_UNIT,
  TOUCH_SENSITIVITY_UNIT,
  settingsStore,
  type Settings,
} from './settings.js';
import { TOUCH_MODE, TouchControls } from './touch.js';
import { VoiceManager } from './voice.js';
import {
  gameStore,
  type HubGroupView,
  type PadOccupant,
  type ScoreboardRow,
} from './store.js';

/** React is updated at 20 Hz; the render loop runs as fast as the display allows. */
const HUD_SYNC_INTERVAL_MS = 50;
/** Prediction errors below this are ignored — they are just float noise. */
const RECONCILE_EPSILON = 0.001;
/** Above this the correction is snapped rather than smoothed (teleport, respawn). */
const SNAP_THRESHOLD = 2.5;
/** Lobby orbit camera: how far behind the avatar it sits, and what it looks at. */
const ORBIT_DISTANCE = 3.4;
const ORBIT_TARGET_HEIGHT = 1.35;
/** Touch auto-fire opens up only after the crosshair has held a target this long. */
const AUTO_FIRE_DWELL_MS = 260;
/** Touch ADS aim assist: hitbox padding that counts as "near", and the slowdown. */
const AIM_ASSIST_PAD = 0.55;
const AIM_ASSIST_FRICTION = 0.5;

export interface EngineCallbacks {
  onHitMarker(killed: boolean, headshot: boolean): void;
  onDamageTaken(): void;
}

/**
 * The client game loop. Everything performance-critical lives here and never
 * touches React: input sampling, prediction, reconciliation, rendering.
 *
 * The netcode model is the standard one for a competitive shooter:
 *   1. Sample input once per fixed tick and apply it locally at once (prediction).
 *   2. Send it, keeping a copy until the server acknowledges it.
 *   3. When a snapshot arrives, reset to the authoritative state and replay every
 *      unacknowledged command (reconciliation).
 *   4. Render remote players in the past, interpolated (see PlayerRenderer).
 */
export class GameEngine {
  private readonly renderer: SceneRenderer;
  private readonly players: PlayerRenderer;
  private readonly avatar: LocalAvatar;
  private readonly effects: EffectsRenderer;
  private readonly viewModel: ViewModel;
  private readonly input: InputManager;
  private readonly touch: TouchControls | null;
  private readonly connection: GameConnection;
  private readonly voice: VoiceManager;
  private readonly diag: DiagOverlay;
  readonly audio = new AudioEngine();

  /** Mirrors the touch auto-fire setting; only consulted in touch mode. */
  private touchAutoFire = true;
  /** True once the current touch-crouch latch has produced a slide. */
  private touchCrouchSlid = false;
  /** How long the crosshair has continuously held a visible enemy, for auto-fire. */
  private autoFireDwellMs = 0;

  /** Local-only simulation used purely for prediction. */
  private readonly sim = new GameSim();
  private localPlayer: PlayerState | null = null;
  private localId: PlayerId = 0;
  private localTeam: TeamId = 0;
  private localName = 'Operative';
  private localCharacter = 0;
  private localSession = '';

  /**
   * Auto-reconnect state. An unexpected drop — a server restart mid-deploy,
   * a network blip — retries quietly with backoff, so a restart is a pause in
   * the lobby rather than a kick to the menu. Deliberate closes never retry:
   * the server always sends an Error frame first when *it* means it
   * ("signed in from another window", "timed out"), which sets
   * `serverRejected`; retrying against those would fight the server.
   */
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private serverRejected = false;

  /** Commands sent but not yet acknowledged, oldest first. */
  private readonly unacked: InputCommand[] = [];
  private lastAckSeq = 0;
  private lastServerTick = 0;
  /**
   * Crouch/jump bits of the last *acknowledged* command. Press-edge detection
   * in the sim reads the previous tick's buttons (`wasCrouchDown`), which is
   * local state, not wire state — so before a reconciliation replay it must be
   * reset to what the server last simulated. Without this, the replay saw the
   * original prediction's "already down" and erased the press edge, which
   * killed a freshly predicted slide one snapshot after C was pressed.
   */
  private ackedCrouchDown = false;
  private ackedJumpDown = false;

  /** Smoothed-out remainder of the last prediction correction. */
  private readonly correction = new THREE.Vector3();

  private running = false;
  /** Set while a deliberate disconnect is in flight, to mute the close handler. */
  private leaving = false;
  private frameHandle = 0;
  private accumulatorMs = 0;
  private lastFrameAtMs = 0;
  private lastHudSyncAtMs = 0;
  private frameCount = 0;
  private fpsWindowStartMs = 0;
  private lastViewYaw = 0;
  private lastViewPitch = 0;
  private previousHealth = PLAYER_MAX_HEALTH;
  private adsHeld = false;
  private scoreboard: ScoreboardRow[] = [];

  /** Mirrors the server's phase. Only `live` accepts gameplay input. */
  private matchPhase: MatchPhase = 'waiting';
  /** The weapon locked in for the coming round, mirrored from the server. */
  private loadout: WeaponId = 'smg';

  /**
   * Death cam. While eliminated the camera borrows a living teammate's eyes;
   * clicking cycles through them, touching WASD detaches back into the free
   * ghost camera. Client-only — the wire already carries everyone's pose.
   */
  private spectating: PlayerId | null = null;
  private prevFireHeld = false;
  private readonly spectatePose: SpectatePose = { x: 0, y: 0, z: 0, yaw: 0, pitch: 0 };

  /** Previous-tick values, used to turn state changes into one-shot sounds. */
  private wasOnGround = true;
  private previousFallSpeed = 0;
  private previousReloadEndsAtMs = 0;
  private previousWeapon: WeaponId = 'smg';
  private lastCountdownSecond = -1;
  private lastPadSecond = -1;
  /** Per-remote-player distance travelled, for footstep cadence. */
  private readonly remoteSteps = new Map<
    PlayerId,
    { x: number; z: number; accumulated: number }
  >();

  /** Shared scratch ray, used by the orbit camera and by touch auto-fire. */
  private readonly scratchVec = new THREE.Vector3();
  private readonly scratchEye = vec3();
  private readonly rayOrigin = vec3();
  private readonly rayDir = vec3();
  private readonly unsubscribeSettings: () => void;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly callbacks: EngineCallbacks,
  ) {
    this.renderer = new SceneRenderer(canvas);
    this.players = new PlayerRenderer(this.renderer.scene);
    this.avatar = new LocalAvatar(this.renderer.scene);
    this.effects = new EffectsRenderer(this.renderer.scene);
    this.viewModel = new ViewModel(this.renderer.viewModelScene);
    this.input = new InputManager(canvas);
    this.input.touchMode = TOUCH_MODE;
    this.touch = TOUCH_MODE ? new TouchControls(canvas, this.input) : null;
    this.sim.predicting = true;

    this.connection = new GameConnection({
      onStatus: (status, detail) => this.handleStatus(status, detail),
      onMessage: (message) => this.handleMessage(message),
    });
    this.voice = new VoiceManager((to, data) =>
      this.connection.sendVoiceSignal(to, data),
    );

    this.diag = new DiagOverlay({
      rttMs: () => this.connection.rttMs,
      interp: () => this.players.consumeDiag(),
    });

    this.renderer.loadMap(this.sim.map);

    // Settings change when the player touches the panel, not per frame, so a
    // subscription is the right shape: React writes the store, the runtime reads
    // it once here and pushes the values into the systems that own them.
    this.applySettings(settingsStore.getState());
    this.unsubscribeSettings = settingsStore.subscribe((state) =>
      this.applySettings(state),
    );
  }

  private applySettings(settings: Settings): void {
    this.input.settings.sensitivity = settings.sensitivity * SENSITIVITY_UNIT;
    this.input.settings.adsSensitivityScale = settings.adsSensitivityScale;
    this.input.settings.invertY = settings.invertY;
    this.input.settings.touchSensitivity =
      settings.touchSensitivity * TOUCH_SENSITIVITY_UNIT;
    this.touchAutoFire = settings.touchAutoFire;
    this.audio.setVolume(settings.masterVolume);
    this.renderer.setBaseFov(settings.fieldOfView);
    this.voice.setReceiveMode(settings.voiceReceive);
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  start(): void {
    if (this.running) return;
    this.running = true;
    this.input.attach();
    this.touch?.attach();
    this.diag.attach();
    this.lastFrameAtMs = performance.now();
    this.fpsWindowStartMs = this.lastFrameAtMs;
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onHubKey);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
    this.frameHandle = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frameHandle);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onHubKey);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    this.input.dispose();
    this.touch?.dispose();
    this.diag.dispose();
    this.connection.disconnect();
  }

  dispose(): void {
    this.stop();
    this.voice.dispose();
    this.unsubscribeSettings();
    this.players.clear();
    this.avatar.dispose();
    this.effects.dispose();
    this.viewModel.dispose();
    this.renderer.dispose();
    this.audio.dispose();
  }

  join(name: string, character = 0, matchId?: string, session = ''): void {
    this.localName = name;
    this.localCharacter = character;
    this.localSession = session;
    this.serverRejected = false;
    this.reconnectAttempts = 0;
    this.viewModel.setCharacter(character);
    // This runs inside the click handler, which is the gesture the browser
    // requires before an AudioContext is allowed to start.
    this.audio.resume();
    gameStore.getState().setPhase('connecting', 'Connecting…');
    this.connection.connect(name, character, matchId, session);
  }

  /**
   * Disconnects and returns to the main menu. The socket close that follows is
   * expected, so it must not surface as a "disconnected" error to the player.
   */
  leave(): void {
    this.leaving = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    // Back to the menu releases the microphone — never record outside a room.
    this.voice.dispose();
    this.connection.disconnect();
    this.localPlayer = null;
    this.localId = 0;
    this.matchPhase = 'waiting';
    this.unacked.length = 0;
    this.lastAckSeq = 0;
    this.scoreboard = [];
    this.players.clear();
    this.avatar.setVisible(false);
    this.remoteSteps.clear();
    this.exitPointerLock();
    gameStore.getState().reset();
  }

  /** Called by the lobby UI when the player picks a weapon for the round. */
  selectLoadout(weapon: WeaponId): void {
    this.loadout = weapon;
    this.input.weapon = weaponIndex(weapon);
    this.audio.resume();
    this.audio.uiSelect();
    gameStore.getState().setMatchState({ loadout: weapon });
    this.connection.sendLoadout(weapon);
  }

  /** Votes to run the game back, from the game-over screen. */
  rematch(): void {
    this.audio.uiSelect();
    this.connection.sendRematch();
  }

  /**
   * Walks this player back to the walkable lobby, staying connected. The server
   * answers with a fresh Welcome for the hub, which resets everything per-match.
   */
  leaveMatch(): void {
    this.audio.uiSelect();
    this.connection.sendLeaveMatch();
  }

  requestPointerLock(): void {
    this.input.requestPointerLock();
  }

  sendChat(text: string): void {
    this.connection.sendChat(text);
  }

  /**
   * Flips the voice-chat microphone. Async because the first enable runs the
   * browser's permission prompt; the store is only marked live once a track
   * actually exists, so the indicator never lies about being hot.
   */
  async toggleMic(): Promise<void> {
    if (!this.connection.isOpen) return;
    const on = !gameStore.getState().micOn;
    if (on) this.audio.uiSelect();
    const granted = await this.voice.setMicEnabled(on);
    gameStore.getState().setMicOn(on && granted);
  }

  /** An on-screen touch button being pressed or released. */
  setTouchButton(bit: number, held: boolean): void {
    this.input.setTouchButton(bit, held);
    // The crouch latch is mirrored in the store so the on-screen button and
    // the held bit can never disagree — the engine unlatches it by itself when
    // a slide ends (see tick), and the button must show that.
    if (bit === Button.Crouch) {
      gameStore.getState().setTouchCrouch(held);
      this.touchCrouchSlid = false;
    }
  }

  /** The touch weapon button: step to the next slot. */
  cycleWeapon(): void {
    this.input.weapon = (this.input.weapon + 1) % 4;
  }

  /** Votes for a map while queued on a pad. Keyboard digits and touch taps. */
  vote(mapIndex: number): void {
    if (!this.inHubQueue() || mapIndex >= MAP_ROTATION.length) return;
    this.audio.uiSelect();
    this.connection.sendVote(mapIndex);
  }

  /** Votes bot difficulty while queued on a vs-bot pad. Keys 5–8 and touch taps. */
  voteDifficulty(tier: number): void {
    if (tier >= BOT_TIERS.length) return;
    const mine = gameStore.getState().hubGroups.find((group) => group.mine);
    if (!mine?.solo || !this.inHubQueue()) return;
    this.audio.uiSelect();
    this.connection.sendVoteDifficulty(tier);
  }

  /** Offers to fight outnumbered from a pad. Keyboard F and touch tap. */
  padReady(): void {
    if (!this.inHubQueue()) return;
    this.audio.uiSelect();
    this.connection.sendPadReady();
  }

  private inHubQueue(): boolean {
    if (this.sim.map.combat || !this.connection.isOpen) return false;
    return gameStore.getState().hubGroups.some((group) => group.mine);
  }

  private readonly onResize = (): void => this.renderer.resize();

  /**
   * Escape releases the cursor, which is the only way out of a round — so the
   * UI needs to know, to offer the pause menu rather than leaving the player
   * staring at a game that no longer responds to the mouse.
   */
  private readonly onPointerLockChange = (): void => {
    gameStore
      .getState()
      .setMatchState({ pointerLocked: document.pointerLockElement !== null });
  };

  // -------------------------------------------------------------------------
  // Networking
  // -------------------------------------------------------------------------

  private handleStatus(status: ConnectionStatus, detail?: string): void {
    const store = gameStore.getState();
    // The player asked to leave, so the close that follows is not a failure.
    if (this.leaving) {
      if (status === 'closed' || status === 'error') this.leaving = false;
      return;
    }
    switch (status) {
      case 'connecting':
        store.setPhase('connecting', 'Connecting…');
        break;
      case 'connected':
        this.reconnectAttempts = 0;
        store.setPhase('connecting', 'Joining match…');
        break;
      case 'closed':
      case 'error':
        if (this.tryReconnect()) break;
        store.setPhase(
          'disconnected',
          detail ?? (status === 'closed' ? 'Disconnected' : 'Connection failed'),
        );
        break;
    }
  }

  /** Schedules a retry after an unexpected drop. False when giving up. */
  private tryReconnect(): boolean {
    // Never joined, told to go away, or already waiting on a timer.
    if (this.localId === 0 || this.serverRejected) return false;
    if (this.reconnectTimer !== null) return true;
    if (this.reconnectAttempts >= 12) return false;
    const backoffMs = Math.min(8000, 500 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts++;
    gameStore.getState().setPhase('connecting', 'Reconnecting…');
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connection.connect(
        this.localName,
        this.localCharacter,
        undefined,
        this.localSession,
      );
    }, backoffMs);
    return true;
  }

  private handleMessage(message: AnyServerMessage): void {
    switch (message.type) {
      case ServerMessage.Welcome: {
        // A Welcome now arrives mid-connection too — the server transfers
        // players between the lobby and pad matches on the same socket — so
        // everything per-match must reset, including the rendered remotes.
        this.localId = message.playerId;
        this.localTeam = message.team;
        this.lastServerTick = message.tick;
        this.sim.tick = message.tick;
        this.sim.players.clear();
        this.players.clear();
        this.remoteSteps.clear();
        this.matchPhase = 'waiting';
        // The server decides the map, not the client's default. Prediction is
        // only valid once both sides are colliding against the same brushes.
        this.setMap(message.mapId);
        this.spectating = null;
        this.localPlayer = this.sim.createPlayer(message.playerId, message.team);
        this.players.setLocalTeam(message.team);
        this.players.setName(message.playerId, this.localName);
        this.players.setCharacter(message.playerId, this.localCharacter);
        this.unacked.length = 0;
        this.lastAckSeq = 0;
        this.ackedCrouchDown = false;
        this.ackedJumpDown = false;
        this.input.yaw = this.localPlayer.yaw;
        this.input.pitch = 0;
        // A mid-match reattach (reload, device switch) resumes holding
        // whatever the frozen seat was carrying; a fresh join gets the
        // default. Without this, prediction yanks the gun back to slot 1.
        this.input.weapon = message.weapon;
        this.loadout = weaponFromIndex(message.weapon);
        // Voice peers belong to the old room; the new roster's PlayerJoined
        // messages rebuild the mesh. The mic itself survives the transfer.
        this.voice.roomChanged(message.playerId, message.team, this.sim.map.combat);
        // Chat is scoped to the room: the server replays the new room's
        // recent history right after this Welcome, so start from clean.
        gameStore.getState().clearChat();
        gameStore.getState().setMatchState({ hubGroups: [] });
        gameStore.getState().setPhase('playing');
        break;
      }
      case ServerMessage.MatchState:
        this.applyMatchState(message);
        break;
      case ServerMessage.HubState:
        this.applyHubState(message);
        break;
      case ServerMessage.Snapshot:
        this.applySnapshot(message);
        break;
      case ServerMessage.Events:
        this.applyEvents(message.events);
        break;
      case ServerMessage.Pong:
        this.connection.recordPong(message.clientTimeMs);
        break;
      case ServerMessage.PlayerJoined:
        this.players.setCharacter(message.playerId, message.character);
        this.players.setName(message.playerId, message.name);
        // Bots have no ears or mic; opening an RTCPeerConnection per bot would
        // hold a dead peer for every one of them.
        if (!isBotId(message.playerId)) {
          this.voice.addPeer(message.playerId, message.team);
        }
        break;
      case ServerMessage.PlayerLeft:
        this.players.remove(message.playerId);
        this.remoteSteps.delete(message.playerId);
        this.voice.removePeer(message.playerId);
        break;
      case ServerMessage.VoiceSignal:
        this.voice.handleSignal(message.from, message.data);
        break;
      case ServerMessage.Error:
        // An Error frame is the server closing on purpose — don't reconnect.
        this.serverRejected = true;
        gameStore.getState().setPhase('disconnected', message.reason);
        break;
      case ServerMessage.Chat:
        // Sender name and team ride on the message itself — replayed history
        // can quote players this client has never seen in a roster.
        gameStore.getState().pushChat({
          from: message.name,
          team: message.team,
          self: message.playerId === this.localId,
          text: message.text,
        });
        break;
    }
  }

  /** Rebuilds collision and the rendered world for a map the server chose. */
  private setMap(mapId: string): void {
    if (mapId !== this.sim.map.id) {
      this.sim.loadMap(mapId);
      this.renderer.loadMap(this.sim.map);
    }
    this.players.setCombat(this.sim.map.combat);
    // Always republished, not just on a change: leaving a match resets the store,
    // so rejoining the same map still has to restore its name for the lobby.
    gameStore.getState().setMap(this.sim.map.id);
  }

  private applyMatchState(message: MatchStateMessage): void {
    const previousPhase = this.matchPhase;
    this.matchPhase = message.phase;
    this.setMap(message.mapId);

    const store = gameStore.getState();
    store.setMatchState({
      matchPhase: message.phase,
      phaseEndsAtMs:
        message.phaseEndsInMs > 0 ? performance.now() + message.phaseEndsInMs : 0,
      teamScores: message.scores,
      roundWins: message.rounds,
      roundNumber: message.roundNumber,
      lastRoundResult: message.lastRoundResult,
      lobby: message.roster.map((entry) => ({
        id: entry.id,
        team: entry.team,
        ready: entry.ready,
        name: entry.id === this.localId ? this.localName : this.players.getName(entry.id),
        isSelf: entry.id === this.localId,
      })),
    });

    if (message.phase === 'loadout' && previousPhase !== 'loadout') {
      // Mirrors the server: only a game-over reopening clears the pick. A
      // transfer arrives via `waiting`, and a pick made in that beat stands.
      if (previousPhase === 'ended') store.setMatchState({ loadout: null });
      this.lastCountdownSecond = -1;
    }
    // The pointer stays locked through the between-rounds banner — the next
    // round starts on its own. Every other non-live phase needs the mouse.
    if (
      message.phase !== 'live' &&
      message.phase !== 'roundEnd' &&
      message.phase !== previousPhase
    ) {
      this.exitPointerLock();
    }
    if (message.phase === 'live' && previousPhase !== 'live') {
      this.previousHealth = PLAYER_MAX_HEALTH;
      // The server's round reset respawns everyone, which refills every stashed
      // magazine. The stash never rides the wire, so prediction must forget its
      // own copy here or the first weapon switch of the round would briefly
      // predict last round's half-empty mag.
      if (this.localPlayer) this.localPlayer.magazines = {};
      // The lobby is permanently 'live' so players can walk; entering it is
      // not a round starting and gets no fanfare.
      if (this.sim.map.combat) this.audio.roundStart();
      this.input.requestPointerLock();
    }
    if (message.phase === 'roundEnd' && previousPhase === 'live') {
      this.audio.roundEnd(message.lastRoundResult === this.localTeam);
    }
    if (message.phase === 'ended' && previousPhase !== 'ended') {
      const mine = message.rounds[this.localTeam];
      const theirs = message.rounds[this.localTeam === 0 ? 1 : 0];
      this.audio.roundEnd(mine > theirs);
    }

    // One beep per remaining second of the lobby countdown.
    if (message.phase === 'loadout') {
      const second = Math.ceil(message.phaseEndsInMs / 1000);
      if (second !== this.lastCountdownSecond && second > 0 && second <= 5) {
        this.lastCountdownSecond = second;
        this.audio.countdown(second === 1);
      }
    }
  }

  private exitPointerLock(): void {
    if (document.pointerLockElement) document.exitPointerLock();
  }

  /**
   * Resolves the wire's index-based pad state into named rows for the overlay.
   * Groups arrive ordered exactly as `padGroups(map)` orders them — both sides
   * derive the list from the same map data.
   */
  private applyHubState(message: HubStateMessage): void {
    const groups = padGroups(this.sim.map);
    const views: HubGroupView[] = message.groups.map((status, index) => {
      const def = groups[index];
      const ready = new Set(status.ready);
      const mine = status.teams.some((team) => team.includes(this.localId));
      const voteCounts: number[] = MAP_ROTATION.map(() => 0);
      for (const vote of status.votes) {
        if (vote.mapIndex < voteCounts.length) voteCounts[vote.mapIndex]!++;
      }
      const difficultyCounts: number[] = BOT_TIERS.map(() => 0);
      for (const vote of status.difficultyVotes) {
        if (vote.tier < difficultyCounts.length) difficultyCounts[vote.tier]!++;
      }
      return {
        id: def?.id ?? `pad-${index}`,
        capacity: def?.capacity ?? 1,
        solo: def?.solo ?? false,
        botsPerPlayer: def?.botsPerPlayer ?? 0,
        countdownEndsAtMs:
          status.countdownEndsInMs > 0
            ? performance.now() + status.countdownEndsInMs
            : 0,
        teams: status.teams.map((team) =>
          team.map((id) => ({
            id,
            name: this.nameOf(id),
            ready: ready.has(id),
            isSelf: id === this.localId,
          })),
        ) as [PadOccupant[], PadOccupant[]],
        voteCounts,
        difficultyCounts,
        myVote:
          status.votes.find((vote) => vote.id === this.localId)?.mapIndex ?? null,
        myDifficultyVote:
          status.difficultyVotes.find((vote) => vote.id === this.localId)?.tier ??
          null,
        mine,
      };
    });
    gameStore.getState().setMatchState({ hubGroups: views });

    // Pad countdown beeps, for the group the local player is standing on.
    const mine = views.find((view) => view.mine);
    if (mine && mine.countdownEndsAtMs > 0) {
      const second = Math.ceil((mine.countdownEndsAtMs - performance.now()) / 1000);
      if (second !== this.lastPadSecond && second >= 1 && second <= 3) {
        this.lastPadSecond = second;
        this.audio.countdown(second === 1);
      }
    } else {
      this.lastPadSecond = -1;
    }
  }

  /**
   * Pad interactions are keyboard-driven because the pointer is locked while
   * walking: digits vote for a map, F offers to fight outnumbered. Lives on
   * the engine, not React — it is gameplay input, not UI.
   */
  private readonly onHubKey = (event: KeyboardEvent): void => {
    // Typing in the menu or account forms must never trigger gameplay keys.
    // (The chat input stops propagation itself, but not every field does.)
    const target = event.target;
    if (
      target instanceof HTMLElement &&
      (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')
    ) {
      return;
    }
    const digit = /^Digit([1-9])$/.exec(event.code);
    if (digit) {
      // 1–3 vote the arena; 5–8 vote bot difficulty on a vs-bot pad. The gap
      // keeps the two ballots from ever being one fat-finger apart.
      const value = Number(digit[1]);
      if (value <= MAP_ROTATION.length) this.vote(value - 1);
      else if (value >= 5) this.voteDifficulty(value - 5);
      return;
    }
    if (event.code === 'KeyF') this.padReady();
    if (event.code === 'KeyV') void this.toggleMic();
  };

  /**
   * Reconciliation. The server's state is truth as of `ackSeq`; everything the
   * player has done since then is replayed on top of it. If the replay lands
   * where prediction already was, nothing visibly changes — which is the
   * common case, and why the local player feels input-lag free.
   */
  private applySnapshot(snapshot: SnapshotMessage): void {
    const player = this.localPlayer;
    if (!player) return;

    this.lastServerTick = snapshot.tick;
    this.players.ingest(snapshot.players, performance.now(), snapshot.tick);
    this.trackRemoteFootsteps(snapshot.players);

    this.scoreboard = [
      {
        id: this.localId,
        name: this.localName,
        team: this.localTeam,
        kills: snapshot.self.kills,
        deaths: snapshot.self.deaths,
        isSelf: true,
      },
      ...snapshot.players.map((entry) => ({
        id: entry.id,
        name: this.players.getName(entry.id),
        team: entry.team,
        kills: entry.kills,
        deaths: entry.deaths,
        isSelf: false,
      })),
    ].toSorted((a, b) => b.kills - a.kills || a.deaths - b.deaths);

    if (snapshot.self.health < this.previousHealth) {
      this.callbacks.onDamageTaken();
      this.audio.damage();
    }
    this.previousHealth = snapshot.self.health;

    const wasAlive = player.alive;
    const nowAlive = (snapshot.self.flags & SnapshotFlag.Alive) !== 0;
    if (wasAlive && !nowAlive) {
      this.audio.death();
      // Straight onto a teammate's shoulder — clicking cycles, WASD detaches.
      this.spectating = this.players.spectateCandidates(this.localTeam)[0] ?? null;
    }
    if (!wasAlive && nowAlive) {
      this.audio.respawn();
      this.spectating = null;
    }

    const predictedX = player.position.x;
    const predictedY = player.position.y;
    const predictedZ = player.position.z;

    // Rewind to authority.
    player.position.x = snapshot.self.position.x;
    player.position.y = snapshot.self.position.y;
    player.position.z = snapshot.self.position.z;
    player.velocity.x = snapshot.self.velocity.x;
    player.velocity.y = snapshot.self.velocity.y;
    player.velocity.z = snapshot.self.velocity.z;
    player.health = snapshot.self.health;
    player.ammo = snapshot.self.ammo;
    player.kills = snapshot.self.kills;
    player.deaths = snapshot.self.deaths;
    player.alive = (snapshot.self.flags & SnapshotFlag.Alive) !== 0;
    player.onGround = (snapshot.self.flags & SnapshotFlag.OnGround) !== 0;
    player.crouching = (snapshot.self.flags & SnapshotFlag.Crouching) !== 0;
    player.sliding = (snapshot.self.flags & SnapshotFlag.Sliding) !== 0;

    this.lastAckSeq = snapshot.ackSeq;
    while (this.unacked.length > 0 && this.unacked[0]!.seq <= snapshot.ackSeq) {
      const acked = this.unacked.shift()!;
      this.ackedCrouchDown = hasButton(acked.buttons, Button.Crouch);
      this.ackedJumpDown = hasButton(acked.buttons, Button.Jump);
    }

    // Rewind the button-edge memory along with the rest of the state, so the
    // replay sees the same press edges the server will (slide trigger, jump
    // buffer) instead of the edges the original prediction already consumed.
    player.wasCrouchDown = this.ackedCrouchDown;
    player.wasJumpDown = this.ackedJumpDown;

    // The slide clocks are not on the wire: prediction already spent the
    // unacked ticks from them, and the replay below spends the same ticks
    // again. Refund them, or a mid-slide reconciliation fast-forwards the
    // timer and ends the predicted slide early — the server keeps pushing the
    // player while the pose snaps upright.
    const refundMs = this.unacked.length * TICK_DT * 1000;
    if (player.sliding) player.slideTimerMs += refundMs;
    if (player.slideCooldownMs > 0) player.slideCooldownMs += refundMs;

    // Replay. Weapon side effects are suppressed: shots were already resolved
    // authoritatively and replaying them would double-count ammo.
    const restoreTick = this.sim.tick;
    for (const command of this.unacked) {
      this.sim.stepPlayer(player, command, { shots: [], hits: [] });
    }
    this.sim.tick = restoreTick;

    // Fold whatever the replay moved us by into a smoothing offset instead of
    // teleporting the camera. Large errors (respawn, teleport) snap instead.
    const errorX = predictedX - player.position.x;
    const errorY = predictedY - player.position.y;
    const errorZ = predictedZ - player.position.z;
    const error = Math.hypot(errorX, errorY, errorZ);
    if (error > RECONCILE_EPSILON && error < SNAP_THRESHOLD) {
      this.correction.set(errorX, errorY, errorZ);
    } else {
      this.correction.set(0, 0, 0);
    }
    this.diag.snapshot(performance.now(), error);
  }

  private applyEvents(events: SimEvents): void {
    for (const shot of events.shots) {
      // The local player's own tracer is spawned at fire time by prediction.
      if (shot.shooter === this.localId) continue;
      // Anchor the tracer (and a flash) to the gun the shooter's model is
      // visibly holding — a shot should leave the barrel you can see, not the
      // eye position the trace actually used. The wire origin is the fallback
      // for shooters with no rendered model yet.
      const fromMuzzle = this.players.muzzleWorldPosition(shot.shooter, this.scratchVec);
      this.effects.spawnTracer(fromMuzzle ? this.scratchVec : shot.origin, shot.end);
      this.effects.spawnImpact(shot.end);
      if (fromMuzzle) this.effects.flashMuzzle(this.scratchVec);
      this.audio.shot(shot.weapon, shot.origin);
      this.audio.impact(shot.end);
    }

    for (const hit of events.hits) {
      if (hit.attacker === this.localId) {
        this.callbacks.onHitMarker(hit.killed, hit.headshot);
        this.audio.hitMarker(hit.killed, hit.headshot);
      }
      if (!hit.killed) continue;
      gameStore.getState().pushKill({
        attacker: this.nameOf(hit.attacker),
        victim: this.nameOf(hit.victim),
        headshot: hit.headshot,
        attackerTeam: this.teamOf(hit.attacker),
        victimTeam: this.teamOf(hit.victim),
      });
    }
  }

  /**
   * Enemy footsteps, derived from how far each remote player has moved between
   * snapshots. Hearing someone approach is core to the genre, and it costs
   * nothing extra on the wire — the positions are already being sent.
   */
  private trackRemoteFootsteps(players: readonly SnapshotPlayer[]): void {
    for (const entry of players) {
      let state = this.remoteSteps.get(entry.id);
      if (!state) {
        this.remoteSteps.set(entry.id, {
          x: entry.position.x,
          z: entry.position.z,
          accumulated: 0,
        });
        continue;
      }

      const moved = Math.hypot(entry.position.x - state.x, entry.position.z - state.z);
      state.x = entry.position.x;
      state.z = entry.position.z;

      const grounded = (entry.flags & SnapshotFlag.OnGround) !== 0;
      const alive = (entry.flags & SnapshotFlag.Alive) !== 0;
      const sliding = (entry.flags & SnapshotFlag.Sliding) !== 0;
      if (!grounded || !alive || sliding) {
        state.accumulated = 0;
        continue;
      }

      state.accumulated += moved;
      if (state.accumulated >= 1.9) {
        state.accumulated = 0;
        this.audio.remoteStep(entry.position);
      }
    }
  }

  private nameOf(id: PlayerId): string {
    return id === this.localId ? this.localName : this.players.getName(id);
  }

  private teamOf(id: PlayerId): TeamId {
    if (id === this.localId) return this.localTeam;
    return this.players.getTeam(id) ?? 1;
  }

  // -------------------------------------------------------------------------
  // Main loop
  // -------------------------------------------------------------------------

  private readonly frame = (nowMs: number): void => {
    if (!this.running) return;
    this.frameHandle = requestAnimationFrame(this.frame);

    const frameDtMs = Math.min(250, nowMs - this.lastFrameAtMs);
    this.lastFrameAtMs = nowMs;
    const dt = frameDtMs / 1000;

    this.accumulatorMs += frameDtMs;
    const tickMs = TICK_DT * 1000;
    // Bound catch-up so a background tab doesn't fire hundreds of ticks at once.
    if (this.accumulatorMs > tickMs * 8) this.accumulatorMs = tickMs * 8;
    while (this.accumulatorMs >= tickMs) {
      this.accumulatorMs -= tickMs;
      this.tick();
    }

    this.input.decayRecoil(dt);
    // Remotes first: the spectator camera reads the pose this pass computes.
    this.players.update(nowMs);
    this.updateCamera(dt);
    this.effects.update(frameDtMs);
    this.renderer.animate(nowMs);
    this.renderer.render();
    this.diag.frame(nowMs);

    this.frameCount++;
    if (nowMs - this.lastHudSyncAtMs >= HUD_SYNC_INTERVAL_MS) {
      this.syncHud(nowMs);
      this.lastHudSyncAtMs = nowMs;
    }
  };

  /** One fixed simulation step: sample, predict, send. */
  private tick(): void {
    const player = this.localPlayer;
    if (!player || !this.connection.isOpen) return;

    // Without pointer lock the player is in a menu: keep the view but drop all
    // actions, so nobody shoots the floor while alt-tabbed. Touch has no
    // pointer lock — there, overlays swallow the touches instead.
    const command = this.input.sample();
    if (!this.input.pointerLocked && !this.input.touchMode) command.buttons = 0;
    // Outside a live round the server freezes everyone; predicting anything else
    // here would be corrected on the next snapshot and read as rubber-banding.
    // This must stay identical to `frozenCommand` on the server.
    if (this.matchPhase !== 'live') {
      command.buttons = 0;
      command.weapon = weaponIndex(this.loadout);
    }
    // In the lobby the camera orbits the avatar, so WASD is remapped to be
    // camera-relative before the command is predicted or sent.
    if (!this.sim.map.combat) this.remapOrbitInput(command);
    this.updateSpectate(command);
    this.applyTouchAssists(command);
    this.adsHeld = hasButton(command.buttons, Button.Ads);

    this.sim.tick++;
    const events: SimEvents = { shots: [], hits: [] };
    const ammoBefore = player.ammo;
    this.sim.stepPlayer(player, command, events);

    // On touch the crouch button is a latch, and a latch that survives the
    // slide would drop the player into a crouch-walk they never asked for —
    // "moving + crouch = slide" means the whole gesture ends when the slide
    // does. A tap while standing still never slides, so that latch stays an
    // actual crouch. State-based rather than edge-based, so a slide ended by
    // a reconciliation replay instead of this tick's step still unlatches.
    if (this.input.touchMode && gameStore.getState().touchCrouch) {
      if (player.sliding) this.touchCrouchSlid = true;
      else if (this.touchCrouchSlid) this.setTouchButton(Button.Crouch, false);
    }

    // Predicted local shot: fire the cosmetics immediately rather than waiting a
    // round trip. The server still decides whether anything was actually hit.
    if (player.ammo < ammoBefore) {
      const def = WEAPONS[weaponFromIndex(command.weapon)];
      this.input.addRecoil(def.recoilPitch, def.recoilYaw);
      this.viewModel.onFire();
      this.audio.shot(player.weapon);
      for (const shot of events.shots) {
        this.effects.spawnTracer(shot.origin, shot.end);
        this.effects.spawnImpact(shot.end);
      }
      this.effects.flashMuzzle(
        this.viewModel
          .muzzleWorldPosition(this.scratchVec)
          .applyMatrix4(this.renderer.camera.matrixWorld),
      );
    }

    this.playMovementAudio(player);

    this.unacked.push(command);
    if (this.unacked.length > INPUT_BUFFER_SIZE) this.unacked.shift();

    this.connection.sendInput(this.sim.tick, this.unacked, this.lastServerTick);
  }

  /**
   * Death-cam controls, evaluated on the same sampled command the tick sends.
   * Click cycles through living teammates (edge-triggered so holding fire does
   * not spin the rotation); any movement key detaches into the free ghost
   * camera the dead body already provides. A target dying mid-watch hops to
   * the next living teammate rather than dumping the viewer on the floor.
   */
  private updateSpectate(command: InputCommand): void {
    const player = this.localPlayer;
    const fireHeld = hasButton(command.buttons, Button.Fire);
    const firePressed = fireHeld && !this.prevFireHeld;
    this.prevFireHeld = fireHeld;

    if (!player || player.alive || !this.sim.map.combat) {
      this.spectating = null;
      return;
    }
    const moveHeld =
      (command.buttons & (Button.Forward | Button.Back | Button.Left | Button.Right)) !==
      0;
    if (moveHeld) {
      this.spectating = null;
      return;
    }
    const candidates = this.players.spectateCandidates(this.localTeam);
    if (candidates.length === 0) {
      this.spectating = null;
      return;
    }
    if (this.spectating === null) {
      if (firePressed) this.spectating = candidates[0]!;
    } else if (firePressed) {
      const next = (candidates.indexOf(this.spectating) + 1) % candidates.length;
      this.spectating = candidates[next]!;
    } else if (!candidates.includes(this.spectating)) {
      this.spectating = candidates[0]!;
    }
  }

  /**
   * Touch aiming aids, both driven by the same crosshair ray:
   *
   * - **Auto-fire** pulls the trigger while the crosshair rests on a living
   *   enemy in the open — the mobile-shooter default, since a thumb that is
   *   busy aiming has nothing left to fire with. It needs the crosshair to
   *   *stay* on the target for a beat first (`AUTO_FIRE_DWELL_MS`): the
   *   instant version beamed people the moment the reticle brushed them,
   *   which out-aimed any human trigger.
   * - **Aim assist** while aiming down sights: with an enemy near the
   *   crosshair, look sensitivity drops so the reticle sticks to the target
   *   instead of skating past.
   *
   * Both are client-side conveniences: they set the same bits a finger would,
   * and the server validates every shot exactly as it always does.
   */
  private applyTouchAssists(command: InputCommand): void {
    const player = this.localPlayer;
    if (
      !this.input.touchMode ||
      !player ||
      !player.alive ||
      !this.sim.map.combat ||
      this.matchPhase !== 'live'
    ) {
      this.input.touchAimAssistScale = 1;
      this.autoFireDwellMs = 0;
      return;
    }

    // Aim exactly where this command says the shot would go.
    eyePosition(player, this.scratchEye);
    const cosPitch = Math.cos(command.pitch);
    this.rayOrigin.x = this.scratchEye.x;
    this.rayOrigin.y = this.scratchEye.y;
    this.rayOrigin.z = this.scratchEye.z;
    this.rayDir.x = -Math.sin(command.yaw) * cosPitch;
    this.rayDir.y = Math.sin(command.pitch);
    this.rayDir.z = -Math.cos(command.yaw) * cosPitch;

    // A visible enemy "near" the crosshair (padded hitboxes) slows ADS look.
    const near = this.players.enemyUnderRay(
      this.localTeam,
      this.rayOrigin.x,
      this.rayOrigin.y,
      this.rayOrigin.z,
      this.rayDir.x,
      this.rayDir.y,
      this.rayDir.z,
      MAX_SHOT_DISTANCE,
      AIM_ASSIST_PAD,
    );
    const nearVisible =
      near !== null && !this.sim.collision.raycast(this.rayOrigin, this.rayDir, near);
    this.input.touchAimAssistScale = nearVisible ? AIM_ASSIST_FRICTION : 1;

    if (!this.touchAutoFire) return;

    // The trigger itself wants the tight hitbox, held long enough to be aim.
    const exact =
      nearVisible &&
      this.players.enemyUnderRay(
        this.localTeam,
        this.rayOrigin.x,
        this.rayOrigin.y,
        this.rayOrigin.z,
        this.rayDir.x,
        this.rayDir.y,
        this.rayDir.z,
        MAX_SHOT_DISTANCE,
      ) !== null;
    this.autoFireDwellMs = exact ? this.autoFireDwellMs + TICK_DT * 1000 : 0;
    if (this.autoFireDwellMs >= AUTO_FIRE_DWELL_MS) command.buttons |= Button.Fire;
  }

  /**
   * Third-person lobby controls. The mouse orbits the camera; the held keys
   * pick a direction relative to it, sent as "walk forward along this yaw" —
   * which is also what turns the avatar to face where it walks. With no keys
   * held the yaw is pinned, so the camera can circle a standing character and
   * look at it from the front: the whole point of the lobby view. Both sides
   * simulate the same remapped command, so prediction cannot drift on this.
   */
  private remapOrbitInput(command: InputCommand): void {
    const moveMask = Button.Forward | Button.Back | Button.Left | Button.Right;
    const mx =
      (hasButton(command.buttons, Button.Left) ? 1 : 0) -
      (hasButton(command.buttons, Button.Right) ? 1 : 0);
    const mz =
      (hasButton(command.buttons, Button.Forward) ? 1 : 0) -
      (hasButton(command.buttons, Button.Back) ? 1 : 0);
    if (mx !== 0 || mz !== 0) {
      command.yaw = this.input.viewYaw + Math.atan2(mx, mz);
      command.buttons = (command.buttons & ~moveMask) | Button.Forward;
    } else {
      command.buttons &= ~moveMask;
      command.yaw = this.localPlayer?.yaw ?? command.yaw;
    }
    command.pitch = 0;
  }

  /**
   * Turns per-tick state changes into one-shot sounds. Driven from the fixed
   * tick rather than the frame, so the cadence does not change with framerate.
   */
  private playMovementAudio(player: PlayerState): void {
    if (player.reloadEndsAtMs > 0 && this.previousReloadEndsAtMs === 0) {
      this.audio.reload(WEAPONS[player.weapon].reloadMs);
    }
    this.previousReloadEndsAtMs = player.reloadEndsAtMs;

    if (player.weapon !== this.previousWeapon) {
      this.audio.weaponSwitch();
      this.previousWeapon = player.weapon;
    }

    if (!player.alive) {
      this.wasOnGround = false;
      return;
    }

    if (this.wasOnGround && !player.onGround && player.velocity.y > 0) this.audio.jump();
    if (!this.wasOnGround && player.onGround) {
      // The impact speed is gone by the time we see onGround, so use last tick's.
      this.audio.land(this.previousFallSpeed < -9);
    }
    if (player.onGround) {
      const speed = Math.hypot(player.velocity.x, player.velocity.z);
      // Sliding is silent-ish on purpose: it is the quiet way to cross open ground.
      if (!player.sliding) this.audio.stride(speed * TICK_DT, speed > 9);
    }
    this.wasOnGround = player.onGround;
    this.previousFallSpeed = player.velocity.y;
  }

  private updateCamera(dt: number): void {
    const player = this.localPlayer;
    const camera = this.renderer.camera;
    if (!player) return;

    // Decay the reconciliation offset to zero over ~100ms.
    this.correction.multiplyScalar(Math.max(0, 1 - dt * 12));

    eyePosition(player, this.scratchEye);
    const pose = this.spectatePose;
    const spectating =
      this.spectating !== null &&
      this.sim.map.combat &&
      this.players.spectatePose(this.spectating, pose);
    this.players.setHidden(spectating ? this.spectating : null);
    if (spectating) {
      this.avatar.setVisible(false);
      camera.position.set(pose.x, pose.y, pose.z);
      camera.rotation.set(pose.pitch, pose.yaw, 0, 'YXZ');
      // Keep the ghost's own view aimed where the teammate looks, so detaching
      // with WASD continues from this orientation instead of snapping to a
      // direction the mouse silently accumulated while watching.
      this.input.yaw = pose.yaw;
      this.input.pitch = pose.pitch;
      this.scratchEye.x = pose.x;
      this.scratchEye.y = pose.y;
      this.scratchEye.z = pose.z;
    } else if (this.sim.map.combat) {
      this.avatar.setVisible(false);
      camera.position.set(
        this.scratchEye.x + this.correction.x,
        this.scratchEye.y + this.correction.y,
        this.scratchEye.z + this.correction.z,
      );
      camera.rotation.set(this.input.viewPitch, this.input.viewYaw, 0, 'YXZ');
    } else {
      this.updateOrbitCamera(player, dt);
    }
    this.audio.setListener(this.scratchEye, this.input.viewYaw);

    // The spectator's view model mirrors what the watched teammate is holding.
    const spectatedWeapon = spectating ? this.players.weaponOf(this.spectating!) : null;
    const weapon = weaponFromIndex(spectatedWeapon ?? this.input.weapon);
    const def = WEAPONS[weapon];
    const ads = spectating ? false : this.adsHeld;
    this.renderer.setFov(ads ? def.adsFovScale : 1);
    // Nothing to hold in the lobby (weapons are dead there) or as a free-flying
    // ghost — but a spectator sees the weapon of whoever they are watching.
    this.viewModel.setVisible(this.sim.map.combat && (player.alive || spectating));
    this.viewModel.setWeapon(weapon);
    this.viewModel.update(
      dt,
      Math.hypot(player.velocity.x, player.velocity.z),
      this.input.viewYaw - this.lastViewYaw,
      this.input.viewPitch - this.lastViewPitch,
      ads,
      player.onGround,
    );
    this.lastViewYaw = this.input.viewYaw;
    this.lastViewPitch = this.input.viewPitch;
  }

  /**
   * The lobby's over-the-shoulder view: the camera circles the player's own
   * avatar so they can actually see the character (and later the skins) they
   * picked. The orbit ray is clamped against the map so walls never end up
   * between the camera and the body.
   */
  private updateOrbitCamera(player: PlayerState, dt: number): void {
    const camera = this.renderer.camera;
    const px = player.position.x + this.correction.x;
    const py = player.position.y + this.correction.y;
    const pz = player.position.z + this.correction.z;

    this.avatar.setAppearance(this.localCharacter, this.localTeam);
    this.avatar.setVisible(true);
    this.scratchVec.set(px, py, pz);
    this.avatar.update(
      dt,
      this.scratchVec,
      player.yaw,
      player.crouching,
      player.sliding,
      player.onGround,
      player.velocity.y,
    );

    const yaw = this.input.viewYaw;
    const pitch = this.input.viewPitch;
    const cosPitch = Math.cos(pitch);
    // Same forward convention as the first-person camera ('YXZ', -z forward).
    const fx = -Math.sin(yaw) * cosPitch;
    const fy = Math.sin(pitch);
    const fz = -Math.cos(yaw) * cosPitch;

    this.rayOrigin.x = px;
    this.rayOrigin.y = py + ORBIT_TARGET_HEIGHT;
    this.rayOrigin.z = pz;
    this.rayDir.x = -fx;
    this.rayDir.y = -fy;
    this.rayDir.z = -fz;
    const hit = this.sim.collision.raycast(this.rayOrigin, this.rayDir, ORBIT_DISTANCE);
    const distance = hit ? Math.max(0.5, hit.distance - 0.2) : ORBIT_DISTANCE;

    camera.position.set(
      this.rayOrigin.x - fx * distance,
      this.rayOrigin.y - fy * distance,
      this.rayOrigin.z - fz * distance,
    );
    camera.rotation.set(pitch, yaw, 0, 'YXZ');
  }

  private syncHud(nowMs: number): void {
    const player = this.localPlayer;
    if (!player) return;

    const elapsed = nowMs - this.fpsWindowStartMs;
    let fps = gameStore.getState().fps;
    if (elapsed >= 500) {
      fps = Math.round((this.frameCount * 1000) / elapsed);
      this.frameCount = 0;
      this.fpsWindowStartMs = nowMs;
    }

    const def = WEAPONS[player.weapon];
    gameStore.getState().syncHud({
      health: Math.max(0, Math.round(player.health)),
      ammo: player.ammo,
      magazine: def.magazine,
      weapon: player.weapon,
      weaponName: def.name,
      reloading: player.reloadEndsAtMs > 0,
      alive: player.alive,
      spectatingName: this.spectating !== null ? this.nameOf(this.spectating) : null,
      kills: player.kills,
      deaths: player.deaths,
      scoreboard: this.scoreboard,
      // Bloom in radians mapped to a pixel gap; the constant is purely visual.
      crosshairSpread: 4 + player.spread * 260,
      pingMs: Math.round(this.connection.rttMs),
      fps,
    });
  }
}
